"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import { GEO_PREF_COOKIE, GEO_PREF_UPDATED_EVENT, parseGeoPrefCookieValue } from "@/lib/geo/geo-pref";
import { requiresVisibleSettlementHint } from "@/lib/pricing/reference-currency-policy";

/** The native settlement currency per channel. */
const CHANNEL_CURRENCY: Record<string, string> = {
	eu: "EUR",
	us: "USD",
};

/**
 * Shows the "checkout settles in X" notice ONLY when the user has selected
 * a reference display currency that differs from the channel's native currency.
 * A `referenceCurrency` that `requiresVisibleSettlementHint` (MXN) shows it always,
 * also before the cart's reference prices have resolved.
 */
export function CartSettlementNotice({
	channel,
	className,
	showDual,
	referenceCurrency,
}: {
	channel: string;
	className?: string;
	showDual?: boolean;
	/** The visitor's resolved reference display currency. */
	referenceCurrency?: string;
}) {
	const t = useTranslations("pricing");
	const [showNotice, setShowNotice] = useState(false);

	useEffect(() => {
		function check() {
			const m = document.cookie.match(new RegExp(`(?:^|; )${GEO_PREF_COOKIE}=([^;]*)`));
			const raw = m ? decodeURIComponent(m[1]) : undefined;
			const pref = parseGeoPrefCookieValue(raw);
			const refCurrency = pref?.referenceCurrency?.toUpperCase();
			const nativeCurrency = CHANNEL_CURRENCY[channel.toLowerCase()] ?? "EUR";
			// Show only if a reference currency is set AND differs from the channel's native currency
			setShowNotice(!!refCurrency && refCurrency !== nativeCurrency);
		}

		check();
		window.addEventListener(GEO_PREF_UPDATED_EVENT, check);
		return () => window.removeEventListener(GEO_PREF_UPDATED_EVENT, check);
	}, [channel]);

	const alwaysVisible = referenceCurrency != null && requiresVisibleSettlementHint(referenceCurrency);
	if (!alwaysVisible && !(showDual ?? showNotice)) return null;

	const isUs = channel.toLowerCase().includes("us");

	return (
		<p
			className={cn(
				"border-t border-border px-5 py-2 text-xs leading-normal text-muted-foreground",
				className,
			)}
		>
			{isUs ? t("settlementUs") : t("settlementEu")}
		</p>
	);
}
